import React, {
  useState,
  useRef,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from 'react';

interface DropdownProps {
  label: React.ReactNode | ((isOpen: boolean) => React.ReactNode); // Accepts either static JSX or a function that takes isOpen
  children: React.ReactNode; // This will be the dropdown items passed as children
  dropIcon?: boolean; // Determines if the dropdown icon should be displayed
  side?: string;
  disabled?: boolean;
  title?: string; // Optional title for the dropdown
  closeOnItemClick?: boolean;
  enableKeyboard?: boolean;
  onKeyDown?: (e: React.KeyboardEvent<HTMLDivElement>) => void;
  onOpenChange?: (open: boolean) => void;
}
export type DropdownHandle = {
  close: () => void;
  open: () => void;
};

const Dropdown = forwardRef<DropdownHandle, DropdownProps>(
  (
    {
      label,
      children,
      dropIcon = false,
      side = 'left',
      disabled = false,
      title = 'Dropdown',
      enableKeyboard = false,
      closeOnItemClick = false,
      onKeyDown,
      onOpenChange,
    },
    ref,
  ) => {
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const setOpen = (open: boolean) => {
      setIsOpen(open);
      onOpenChange?.(open);
    };
    useImperativeHandle(ref, () => ({
      close: () => setOpen(false),
      open: () => setOpen(true),
    }));

    const toggleDropdown = () => {
      if (!disabled) setOpen(!isOpen);
    };

    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };

    useEffect(() => {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, []);

    const renderLabel = typeof label === 'function' ? label(isOpen) : label;
    return (
      <div
        ref={dropdownRef}
        className={`relative inline-block text-left outline-none focus:outline-none focus:ring-0 focus:ring-offset-0 ${
          disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
        }`}
        title={title}
        tabIndex={enableKeyboard && !disabled ? 0 : undefined}
        onKeyDown={
          enableKeyboard && !disabled
            ? (e) => {
                onKeyDown?.(e);
                if (e.defaultPrevented) return;

                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  toggleDropdown();
                }
                if (e.key === 'Escape') {
                  e.preventDefault();
                  setOpen(false);
                }
              }
            : undefined
        }
      >
        <span
          onClick={toggleDropdown}
          className={`inline-flex justify-between py-2 text-sm font-medium text-gray-700 focus:outline-none ${
            disabled ? 'text-gray-400' : 'text-gray-700'
          }`}
        >
          {renderLabel}
          {dropIcon && (
            <svg
              className="ml-2 -mr-1 h-5 w-5"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          )}
        </span>
        {isOpen && !disabled && (
          <div
            className={`origin-top-right absolute ${side}-0 mt-2 ring-black ring-opacity-5 focus:outline-none z-50`}
          >
            <div onClick={closeOnItemClick ? toggleDropdown : undefined}>
              {children}
            </div>
          </div>
        )}
      </div>
    );
  },
);
Dropdown.displayName = 'Dropdown';
export default Dropdown;
